import React, { Suspense, lazy, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { NotificationsProvider } from "@/context/NotificationsContext";
import { ConfigProvider } from "@/context/ConfigContext";
import { UnsavedChangesProvider } from "@/context/UnsavedChangesContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell from "@/components/AppShell";
import OperatorHomeRoute from "@/components/OperatorHomeRoute";
import DashboardRoute from "@/components/DashboardRoute";
import HomeRedirect from "@/components/HomeRedirect";
import LoginGate from "@/components/LoginGate";
import RecognitionRoute from "@/components/RecognitionRoute";
import CreateRequestRoute from "@/components/CreateRequestRoute";

// Code-splitting por rota (Bloco 1): tudo fora do caminho crítico inicial
// (Login mantém-se estático) é carregado sob procura via React.lazy + Suspense.
// Nenhuma rota é removida nem alterada — só a forma de importação muda.
// Quando o módulo já está em memória, o lazy recebe um thenable síncrono e não
// suspende: sem fallback, o React 19 não aplica o atraso de ~300 ms à revelação.
function preloadable(factory) {
  let mod = null;
  let pending = null;
  const load = () => {
    pending ??= factory().then(
      (m) => (mod = m),
      (e) => {
        pending = null;
        throw e;
      },
    );
    return pending;
  };
  const Component = lazy(() => (mod ? { then: (resolve) => resolve(mod) } : load()));
  return [Component, load];
}

const ChangePassword = lazy(() => import("@/pages/ChangePassword"));
// Item 4 — o Dashboard é a rota de aterragem: expomos a factory do import
// para poder pré-carregar o chunk DURANTE o bootstrap de auth.
const [Dashboard, importDashboard] = preloadable(() => import("@/pages/Dashboard"));
const DashboardLegacy = lazy(() => import("@/pages/DashboardLegacy"));
const [Hoje, importHoje] = preloadable(() => import("@/pages/Hoje"));
const [Pedidos, importPedidos] = preloadable(() => import("@/pages/Pedidos"));
const [Agenda, importAgenda] = preloadable(() => import("@/pages/Agenda"));
const [PedidoNovo, importPedidoNovo] = preloadable(() => import("@/pages/PedidoNovo"));
const [PedidoDetalhe, importPedidoDetalhe] = preloadable(() => import("@/pages/PedidoDetalhe"));
const Clientes = lazy(() => import("@/pages/Clientes"));
const ClienteDetalhe = lazy(() => import("@/pages/ClienteDetalhe"));
const Fornecedores = lazy(() => import("@/pages/Fornecedores"));
const Notificacoes = lazy(() => import("@/pages/Notificacoes"));
const Evolucao = lazy(() => import("@/pages/Evolucao"));
const [Adocao, importAdocao] = preloadable(() => import("@/pages/equipa/Adocao"));
const Perfil = lazy(() => import("@/pages/Perfil"));
const Utilizadores = lazy(() => import("@/pages/admin/Utilizadores"));
const Configuracoes = lazy(() => import("@/pages/admin/Configuracoes"));
const AuditLog = lazy(() => import("@/pages/admin/AuditLog"));
const Laboratorio = lazy(() => import("@/pages/admin/Laboratorio"));

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-bg text-ink-secondary text-sm">
      A carregar…
    </div>
  );
}

// Item 4 — prefetch em idle das rotas principais depois da app estabilizar.
// Baixo risco: só dispara imports (mesmas factories do lazy → chunk cacheado,
// sem requests duplicados) e não altera comportamento funcional.
const IDLE_ROUTE_PREFETCHERS = [
  importPedidos,
  importAgenda,
  importPedidoDetalhe,
  importHoje,
  () => import("@/pages/Clientes"),
  () => import("@/pages/Fornecedores"),
];

// Entrada directa (recarga ou link): o chunk da rota de entrada também é
// pedido durante o bootstrap de auth, para já estar em memória quando renderiza.
function entryRouteImporter(pathname) {
  if (pathname === "/hoje") return importHoje;
  if (pathname === "/pedidos") return importPedidos;
  if (pathname === "/pedidos/novo") return importPedidoNovo;
  if (pathname.startsWith("/pedidos/")) return importPedidoDetalhe;
  if (pathname === "/agenda") return importAgenda;
  if (pathname === "/equipa/adocao") return importAdocao;
  return null;
}

function usePrefetchRoutes() {
  useEffect(() => {
    // Começa já o download do Dashboard, sobrepondo-o ao bootstrap de auth.
    importDashboard();
    entryRouteImporter(window.location.pathname)?.();
    const ric = window.requestIdleCallback || ((cb) => setTimeout(() => cb(), 1500));
    const cancel = window.cancelIdleCallback || clearTimeout;
    const id = ric(() => { IDLE_ROUTE_PREFETCHERS.forEach((p) => p()); });
    return () => cancel(id);
  }, []);
}

function App() {
  usePrefetchRoutes();
  return (
    <AuthProvider>
      <ConfigProvider>
      <NotificationsProvider>
        <BrowserRouter>
        <UnsavedChangesProvider>
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: "#FFFFFF",
              color: "#111827",
              border: "1px solid #E5E7EB",
              borderRadius: "10px",
            },
          }}
        />
        <ErrorBoundary>
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/login" element={<LoginGate />} />
          <Route
            path="/change-password"
            element={
              <ProtectedRoute>
                <ChangePassword />
              </ProtectedRoute>
            }
          />
          <Route
            element={
              <ProtectedRoute>
                <AppShell />
              </ProtectedRoute>
            }
          >
            <Route path="/" element={<HomeRedirect />} />
            <Route
              path="/dashboard"
              element={
                <DashboardRoute>
                  <Dashboard />
                </DashboardRoute>
              }
            />
            <Route
              path="/hoje"
              element={
                <OperatorHomeRoute>
                  <Hoje />
                </OperatorHomeRoute>
              }
            />
            <Route path="/dashboards/legacy" element={<DashboardLegacy />} />
            <Route path="/pedidos" element={<Pedidos />} />
            <Route
              path="/pedidos/novo"
              element={
                <CreateRequestRoute>
                  <PedidoNovo />
                </CreateRequestRoute>
              }
            />
            <Route path="/pedidos/:id" element={<PedidoDetalhe />} />
            <Route path="/agenda" element={<Agenda />} />
            <Route
              path="/clientes"
              element={
                <ProtectedRoute roles={["admin", "gestor", "operador"]}>
                  <Clientes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clientes/:id"
              element={
                <ProtectedRoute roles={["admin", "gestor", "operador"]}>
                  <ClienteDetalhe />
                </ProtectedRoute>
              }
            />
            <Route
              path="/fornecedores"
              element={
                <ProtectedRoute roles={["admin", "gestor", "operador"]}>
                  <Fornecedores />
                </ProtectedRoute>
              }
            />
            <Route path="/notificacoes" element={<Notificacoes />} />
            <Route
              path="/evolucao"
              element={
                <RecognitionRoute>
                  <Evolucao />
                </RecognitionRoute>
              }
            />
            {/* `/reconhecimento` foi a rota da primeira versão. Fica como
                redirect permanente: há links em notificações e em conversas que
                ainda a usam, e um 404 não é a resposta certa a um link nosso. */}
            <Route path="/reconhecimento" element={<Navigate to="/evolucao" replace />} />
            <Route
              path="/equipa/adocao"
              element={
                <ProtectedRoute roles={["admin", "gestor"]}>
                  <Adocao />
                </ProtectedRoute>
              }
            />
            <Route path="/perfil" element={<Perfil />} />
            <Route
              path="/admin/utilizadores"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <Utilizadores />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/configuracoes"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <Configuracoes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/audit-log"
              element={
                <ProtectedRoute roles={["admin", "gestor"]}>
                  <AuditLog />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/laboratorio"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <Laboratorio />
                </ProtectedRoute>
              }
            />
            {/* Catch-all dentro da protected layout: só é avaliado depois de a
                auth estar resolvida (AppShell/<Outlet/> montado), evitando que
                deep-links/F5 a rotas válidas caíssem aqui durante o loading. */}
            <Route path="*" element={<HomeRedirect />} />
          </Route>
        </Routes>
        </Suspense>
        </ErrorBoundary>
        </UnsavedChangesProvider>
        </BrowserRouter>
      </NotificationsProvider>
      </ConfigProvider>
    </AuthProvider>
  );
}

export default App;
